'use strict';

/**
 * @typedef {'small' | 'large' | 'extra-large'} ModalSize
 */

/**
 * @typedef {'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right'} ToastPlacement
 */

/**
 * @typedef {Object} ToastOptions
 *
 * @property {string} [title=''] - Always plain text.
 * @property {string} [message=''] - Always HTML (caller must sanitize).
 * @property {string} [toastClass=''] - Extra class(es) on the root `.toast` (e.g. `toast-error`).
 * @property {ToastPlacement} [placement='top-right'] - Viewport corner/edge when using the default host (ignored if `container` is set).
 * @property {string|HTMLElement|JQuery} [container=null] - Append target; defaults to a fixed `.toast-container` for `placement`.
 * @property {boolean} [animation=true] - Bootstrap Toast: CSS fade.
 * @property {boolean} [autohide=true] - Bootstrap Toast: auto-hide.
 * @property {number} [delay=5000] - Bootstrap Toast: autohide delay (ms).
 * @property {function(JQuery.TriggeredEvent): void} [onShow=null]
 * @property {function(JQuery.TriggeredEvent): void} [onShown=null]
 * @property {function(JQuery.TriggeredEvent): void} [onHide=null]
 * @property {function(JQuery.TriggeredEvent): void} [onHidden=null]
 */

/**
 * @typedef {Object} ModalOptions
 *
 * @property {string} [title=''] - Always plain text.
 * @property {string} [body=''] - Always HTML (caller must sanitize).
 * @property {string} [modalClass=''] - Extra class(es) on the root `.modal` element.
 * @property {boolean} [animation=true] - When true, adds Bootstrap `fade` on the root `.modal`.
 * @property {string} [titleClass='sub-title-xl'] - Class(es) on `.modal-title`.
 * @property {boolean} [scrollable=false] - Adds `modal-dialog-scrollable`.
 * @property {boolean} [centered=false] - Adds `modal-dialog-centered`.
 * @property {ModalSize|null} [size=null] - `small` / `large` / `extra-large`; omit for default width.
 * @property {boolean|null} [close=null] - Header close (X). Auto: `true` when `cancelText` is set, otherwise `false`.
 * @property {string} [confirmText=null] - Confirm label; omit to hide confirm.
 * @property {string} [confirmClass='btn-primary']
 * @property {function(JQuery.ClickEvent, JQuery): (void|Promise<void>)} [onConfirm=null] - Sync or async. Call `e.preventDefault()` (or throw) to keep the modal open. Confirm is disabled while awaiting.
 * @property {string} [cancelText=null] - Cancel label; omit to hide cancel (no default label).
 * @property {string} [cancelClass='btn-secondary']
 * @property {function(JQuery.ClickEvent, JQuery): (void|Promise<void>)} [onCancel=null] - Sync or async. Call `e.preventDefault()` to keep the modal open.
 * @property {boolean|'static'|null} [backdrop=null] - Auto: `true` when `cancelText` is set, otherwise `'static'`.
 * @property {boolean|null} [keyboard=null] - ESC dismiss. Auto: `true` when `cancelText` is set, otherwise `false`.
 * @property {boolean} [focus=true]
 * @property {boolean} [dismissOthers=false] - When true, hide every other open `.modal.show` and wait for `hidden.bs.modal` before showing this one (page-mounted modals are only hidden, not disposed).
 * @property {function(JQuery.TriggeredEvent): void} [onShow=null]
 * @property {function(JQuery.TriggeredEvent): void} [onShown=null]
 * @property {function(JQuery.TriggeredEvent): void} [onHide=null]
 * @property {function(JQuery.TriggeredEvent): void} [onHidden=null]
 * @property {function(JQuery.TriggeredEvent): void} [onHidePrevented=null]
 */

/**
 * Bootstrap 4 toast / modal feedback helpers (ephemeral DOM, cleaned up on hide).
 */
export const feedback = {
  /**
   * Modal size → Bootstrap dialog class.
   *
   * @private
   * @readonly
   *
   * @type {Record<ModalSize, string>}
   */
  SIZE_MAP: {
    small: 'modal-sm',
    large: 'modal-lg',
    'extra-large': 'modal-xl'
  },

  /**
   * Toast placement → fixed-position styles for the default host.
   *
   * @private
   * @readonly
   *
   * @type {Record<ToastPlacement, Record<string, string>>}
   */
  PLACEMENT_MAP: {
    'top-left': { top: '0', left: '0', right: 'auto', bottom: 'auto', transform: 'none' },
    'top-center': { top: '0', left: '50%', right: 'auto', bottom: 'auto', transform: 'translateX(-50%)' },
    'top-right': { top: '0', right: '0', left: 'auto', bottom: 'auto', transform: 'none' },
    'bottom-left': { bottom: '0', left: '0', right: 'auto', top: 'auto', transform: 'none' },
    'bottom-center': { bottom: '0', left: '50%', right: 'auto', top: 'auto', transform: 'translateX(-50%)' },
    'bottom-right': { bottom: '0', right: '0', left: 'auto', top: 'auto', transform: 'none' }
  },

  /**
   * jQuery event namespace for reliable rebind / cleanup.
   *
   * @private
   * @readonly
   *
   * @type {string}
   */
  EVENT_NS: '.feedback',

  /**
   * @private
   * @readonly
   *
   * @type {ModalOptions}
   */
  MODAL_DEFAULTS: {
    title: '',
    body: '',
    modalClass: '',
    animation: true,
    titleClass: 'sub-title-xl',
    scrollable: false,
    centered: false,
    size: null,
    close: null,
    confirmText: null,
    confirmClass: 'btn-primary',
    onConfirm: null,
    cancelText: null,
    cancelClass: 'btn-secondary',
    onCancel: null,
    backdrop: null,
    keyboard: null,
    focus: true,
    dismissOthers: false,
    onShow: null,
    onShown: null,
    onHide: null,
    onHidden: null,
    onHidePrevented: null
  },

  /**
   * @private
   * @readonly
   *
   * @type {ToastOptions}
   */
  TOAST_DEFAULTS: {
    title: '',
    message: '',
    toastClass: '',
    placement: 'top-right',
    container: null,
    animation: true,
    autohide: true,
    delay: 5000,
    onShow: null,
    onShown: null,
    onHide: null,
    onHidden: null
  },

  /**
   * In-flight modal Promise settle callback.
   *
   * @private
   *
   * @type {function(boolean): void|null}
   */
  pendingModalResolve: null,

  /**
   * Currently mounted feedback modal (ephemeral).
   *
   * @private
   *
   * @type {JQuery|null}
   */
  $activeModal: null,

  /**
   * Show a confirm/alert modal. Resolves when the modal is fully hidden.
   * With `dismissOthers`, other open Bootstrap modals are hidden first.
   *
   * @param {ModalOptions} [options={}]
   *
   * @returns {Promise<boolean>} `true` on confirm, `false` on cancel / dismiss.
   */
  modal(options = {}) {
    return new Promise((resolve) => {
      this.disposeActiveModal(false);

      const opts = this.normalizeModalOptions(options);
      const $modal = this.buildModal(opts);
      const state = { isConfirmed: false };

      $('body').append($modal);
      this.$activeModal = $modal;
      this.pendingModalResolve = resolve;

      this.renderButtons($modal, $modal.find('.modal-footer'), opts, state);
      this.bindModalLifecycle($modal, opts, state);

      const showFeedbackModal = () => {
        if (!this.$activeModal || this.$activeModal[0] !== $modal[0]) {
          return;
        }

        $modal.modal({
          backdrop: opts.backdrop,
          keyboard: opts.keyboard,
          focus: opts.focus,
          show: true
        });
      };

      if (opts.dismissOthers === true) {
        const $modals = $('.modal.show').not($modal);

        this.hideOpenModals($modals).then(showFeedbackModal);

        return;
      }

      showFeedbackModal();
    });
  },

  /**
   * Show an auto-disposing toast.
   *
   * @param {ToastOptions} [options={}]
   *
   * @returns {void}
   */
  toast(options = {}) {
    const opts = this.mergeOptions(this.TOAST_DEFAULTS, options);
    const $container = this.resolveContainer(opts.container, opts.placement);
    const $toast = this.buildToast(opts);

    $container.append($toast);
    this.bindToastLifecycle($toast, opts);

    $toast.toast({
      animation: opts.animation,
      autohide: opts.autohide,
      delay: opts.delay
    });

    $toast.toast('show');
  },

  /**
   * Shallow-merge defaults with caller options (does not mutate defaults).
   *
   * @private
   *
   * @param {Object} defaults
   * @param {Object} options
   *
   * @returns {Object}
   */
  mergeOptions(defaults, options) {
    return Object.assign({}, defaults, options);
  },

  /**
   * Merge modal defaults and resolve auto `close` / `backdrop` / `keyboard` from `cancelText`.
   *
   * @private
   *
   * @param {ModalOptions} options
   *
   * @returns {ModalOptions}
   */
  normalizeModalOptions(options) {
    const opts = this.mergeOptions(this.MODAL_DEFAULTS, options);
    const hasCancel = !!opts.cancelText;

    if (opts.close === null) {
      opts.close = hasCancel;
    }

    if (opts.backdrop === null) {
      opts.backdrop = hasCancel ? true : 'static';
    }

    if (opts.keyboard === null) {
      opts.keyboard = hasCancel;
    }

    return opts;
  },

  /**
   * Build an ephemeral Bootstrap modal from options.
   *
   * @private
   *
   * @param {ModalOptions} opts
   *
   * @returns {JQuery}
   */
  buildModal(opts) {
    const rootClass = ['modal'];
    const dialogClass = ['modal-dialog'];

    if (opts.animation) {
      rootClass.push('fade');
    }

    if (opts.modalClass) {
      rootClass.push(opts.modalClass);
    }

    if (opts.centered) {
      dialogClass.push('modal-dialog-centered');
    }

    if (opts.scrollable) {
      dialogClass.push('modal-dialog-scrollable');
    }

    if (opts.size && this.SIZE_MAP[opts.size]) {
      dialogClass.push(this.SIZE_MAP[opts.size]);
    }

    const titleClass = opts.titleClass ? 'modal-title ' + opts.titleClass : 'modal-title';
    const closeHtml = opts.close
      ? '<button type="button" class="close" data-dismiss="modal" aria-label="Close">' +
          '<span class="label">close</span>' +
          '<i class="icon icon-close"></i>' +
        '</button>'
      : '';

    const $modal = $(
      '<div class="' + rootClass.join(' ') + '" tabindex="-1" role="dialog" aria-hidden="true">' +
        '<div class="' + dialogClass.join(' ') + '" role="document">' +
          '<div class="modal-content">' +
            '<div class="modal-header">' +
              '<h4 class="' + titleClass + '"></h4>' +
              closeHtml +
            '</div>' +
            '<div class="modal-body"></div>' +
            '<div class="modal-footer"></div>' +
          '</div>' +
        '</div>' +
      '</div>'
    );

    $modal.find('.modal-title').text(opts.title || '');
    $modal.find('.modal-body').html(opts.body || '');

    return $modal;
  },

  /**
   * Build confirm/cancel footer buttons.
   *
   * @private
   *
   * @param {JQuery} $modal
   * @param {JQuery} $footer
   * @param {ModalOptions} opts
   * @param {{ isConfirmed: boolean }} state
   *
   * @returns {void}
   */
  renderButtons($modal, $footer, opts, state) {
    if (!opts.cancelText && !opts.confirmText) {
      $footer.remove();

      return;
    }

    if (opts.cancelText) {
      const $btnCancel = $('<button type="button" class="btn cancel"></button>')
        .addClass(opts.cancelClass)
        .text(opts.cancelText);

      $btnCancel.on('click' + this.EVENT_NS, async (e) => {
        if ($btnCancel.prop('disabled')) {
          return;
        }

        $btnCancel.prop('disabled', true);

        try {
          if (typeof opts.onCancel === 'function') {
            await opts.onCancel(e, $modal);
          }

          if (!e.isDefaultPrevented()) {
            $modal.modal('hide');

            return;
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('[Feedback] onCancel failed:', err);
        }

        $btnCancel.prop('disabled', false);
      });

      $footer.append($btnCancel);
    }

    if (opts.confirmText) {
      const $btnConfirm = $('<button type="button" class="btn confirm"></button>')
        .addClass(opts.confirmClass)
        .text(opts.confirmText);

      $btnConfirm.on('click' + this.EVENT_NS, async (event) => {
        if ($btnConfirm.prop('disabled')) {
          return;
        }

        $btnConfirm.prop('disabled', true);

        try {
          if (typeof opts.onConfirm === 'function') {
            await opts.onConfirm(event, $modal);
          }

          if (!event.isDefaultPrevented()) {
            state.isConfirmed = true;
            $modal.modal('hide');

            return;
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('[Feedback] onConfirm failed:', err);
        }

        $btnConfirm.prop('disabled', false);
      });

      $footer.append($btnConfirm);
    }
  },

  /**
   * Hide the given open Bootstrap modals and resolve when all have finished
   * hiding. Does not dispose or remove page-mounted modals.
   *
   * @private
   *
   * @param {JQuery} $modals
   *
   * @returns {Promise<void[]>}
   */
  hideOpenModals($modals) {
    if (!$modals || !$modals.length) {
      return Promise.resolve([]);
    }

    const hides = $modals
      .toArray()
      .map((modal) => new Promise((resolve) => $(modal).one('hidden.bs.modal', resolve).modal('hide')))
    ;

    return Promise.all(hides);
  },

  /**
   * Tear down the active modal (if any) and optionally settle its Promise.
   *
   * @private
   *
   * @param {boolean} confirmed
   *
   * @returns {void}
   */
  disposeActiveModal(confirmed) {
    this.settlePendingModal(confirmed);

    if (!this.$activeModal) {
      return;
    }

    const $modal = this.$activeModal;

    this.$activeModal = null;
    $modal.off(this.EVENT_NS);
    $modal.modal('dispose');
    $modal.remove();
    $('.modal-backdrop').remove();
    $('body').removeClass('modal-open').css('padding-right', '');
  },

  /**
   * Resolve and clear an in-flight modal Promise, if any.
   *
   * @private
   *
   * @param {boolean} confirmed
   *
   * @returns {void}
   */
  settlePendingModal(confirmed) {
    if (typeof this.pendingModalResolve !== 'function') {
      return;
    }

    const resolve = this.pendingModalResolve;

    this.pendingModalResolve = null;
    resolve(confirmed);
  },

  /**
   * Bind Bootstrap modal lifecycle events; dispose DOM and settle Promise on hide.
   *
   * @private
   *
   * @param {JQuery} $modal
   * @param {ModalOptions} opts
   * @param {{ isConfirmed: boolean }} state
   *
   * @returns {void}
   */
  bindModalLifecycle($modal, opts, state) {
    const ns = this.EVENT_NS;

    if (typeof opts.onShow === 'function') {
      $modal.one('show.bs.modal' + ns, opts.onShow);
    }

    if (typeof opts.onShown === 'function') {
      $modal.one('shown.bs.modal' + ns, opts.onShown);
    }

    if (typeof opts.onHide === 'function') {
      $modal.one('hide.bs.modal' + ns, opts.onHide);
    }

    if (typeof opts.onHidePrevented === 'function') {
      $modal.on('hidePrevented.bs.modal' + ns, opts.onHidePrevented);
    }

    $modal.one('hidden.bs.modal' + ns, (e) => {
      const confirmed = state.isConfirmed;

      if (typeof opts.onHidden === 'function') {
        opts.onHidden(e);
      }

      if (this.$activeModal && this.$activeModal[0] === $modal[0]) {
        this.$activeModal = null;
      }

      $modal.off(ns);
      $modal.modal('dispose');
      $modal.remove();
      this.settlePendingModal(confirmed);
    });
  },

  /**
   * Build a toast element from options.
   *
   * @private
   *
   * @param {ToastOptions} opts
   *
   * @returns {JQuery}
   */
  buildToast(opts) {
    const $toast = $(
      '<div class="toast" role="alert" aria-live="assertive" aria-atomic="true">' +
        '<div class="toast-header">' +
          '<strong class="mr-auto toast-title"></strong>' +
          '<button type="button" class="ml-2 mb-1 close" data-dismiss="toast" aria-label="Close">' +
            '<span aria-hidden="true">&times;</span>' +
          '</button>' +
        '</div>' +
        '<div class="toast-body"></div>' +
      '</div>'
    );

    if (opts.toastClass) {
      $toast.addClass(opts.toastClass);
    }

    $toast.find('.toast-title').text(opts.title || '');
    $toast.find('.toast-body').html(opts.message || '');

    return $toast;
  },

  /**
   * Bind Bootstrap toast lifecycle events and dispose the node on hide.
   *
   * @private
   *
   * @param {JQuery} $toast
   * @param {ToastOptions} opts
   *
   * @returns {void}
   */
  bindToastLifecycle($toast, opts) {
    const ns = this.EVENT_NS;

    if (typeof opts.onShow === 'function') {
      $toast.one('show.bs.toast' + ns, opts.onShow);
    }

    if (typeof opts.onShown === 'function') {
      $toast.one('shown.bs.toast' + ns, opts.onShown);
    }

    if (typeof opts.onHide === 'function') {
      $toast.one('hide.bs.toast' + ns, opts.onHide);
    }

    $toast.one('hidden.bs.toast' + ns, (e) => {
      if (typeof opts.onHidden === 'function') {
        opts.onHidden(e);
      }

      $toast.off(ns);
      $toast.toast('dispose');
      $toast.remove();
    });
  },

  /**
   * Resolve a toast append container (create a fixed host for `placement` when needed).
   *
   * @private
   *
   * @param {string|HTMLElement|JQuery|null|undefined} target
   * @param {ToastPlacement} [placement='top-right']
   *
   * @returns {JQuery}
   */
  resolveContainer(target, placement = 'top-right') {
    if (target) {
      return $(target);
    }

    const key = Object.hasOwn(this.PLACEMENT_MAP, placement) ? placement : 'top-right';
    const selector = '.toast-container[data-placement="' + key + '"]';
    let $container = $(selector);

    if (!$container.length) {
      const pos = this.PLACEMENT_MAP[key];

      $container = $('<div class="toast-container position-fixed p-3"></div>')
        .attr('data-placement', key)
        .css({
          zIndex: 1080,
          top: pos.top,
          right: pos.right,
          bottom: pos.bottom,
          left: pos.left,
          transform: pos.transform
        });

      $('body').append($container);
    }

    return $container;
  }
};
